/**
 * Wire types for the Gateway's /api/org/* plane.
 *
 * Mirrors Gateway/src/routes/org.route.ts. Keep in step with it — this file is
 * the contract, and every service returns these shapes rather than `any`.
 */

export type SeatStatus = "invited" | "active" | "deactivated";
export type OrgRole = "owner" | "admin" | "member";
export type OrgStatus = "pending" | "active" | "suspended" | "churned";

export interface SeatCounts {
	/** null = unlimited (flat-fee contracts). NOT zero. */
	purchased: number | null;
	active: number;
	invited: number;
	/** null = unlimited. */
	available: number | null;
}

export interface OrgSummary {
	id: string;
	slug: string;
	name: string;
	status: OrgStatus;
	/** The CALLER's role in this org — drives which controls are offered. */
	role: OrgRole;
	contract_starts_at: string | null;
	/** null means open-ended — nothing expires. */
	contract_ends_at: string | null;
	/** Seats to apply at the next renewal. null = no change requested. */
	seats_scheduled?: number | null;
	settings: OrgSettings;
	auth_policy: OrgAuthPolicy;
	/** Where invoices go. null until the owner sets it at setup. */
	billing_email: string | null;
	/** The org's own stated intent, layered under its teams'. `{}` when unset. */
	intent: OrgIntent;
}

export interface OrgSettings {
	hostname?: string;
	logo_url?: string;
	accent?: string;
	modules?: string[];
	default_domains?: string[];
}

export interface OrgAuthPolicy {
	methods?: string[];
	auto_join_domains?: string[];
	enforce_sso?: boolean;
}

export interface SlackConnection {
	connected: boolean;
	teamId?: string;
	teamName?: string;
	installedAt?: string;
	updatedAt?: string;
}

export type JiraConnection =
	| { connected: false }
	| {
			connected: true;
			siteUrl: string;
			webhookExpiresAt: string;
			/** Who posts the comments. Older backends send none of the three fields below. */
			commentIdentity?: "forge" | "oauth-user";
			/** Whether the Curo app is installed on the connected site (only meaningful for "forge"). */
			forgeReady?: boolean;
			/** Atlassian's installation link for the Curo app, when Curo has one for this environment. */
			forgeInstallUrl?: string;
	  };

/** A project on the connected Jira site. `id` is Jira's numeric project id, as a string. */
export interface JiraSiteProject {
	id: string;
	key: string;
	name: string;
}

/** A project an org admin approved for work personalization. `approvedBy` is the approver's user id. */
export interface JiraApprovedProject {
	id: string;
	name: string;
	approvedBy: string;
	approvedAt: string;
}

export interface JiraProjects {
	projects: JiraSiteProject[];
	approved: JiraApprovedProject[];
}

/** One approved project's open work, as the mirror holds it. */
export interface JiraProjectHealth {
	id: string;
	name: string;
	approvedAt: string;
	openTickets: number;
	assignedTickets: number;
	/** Curo is still reading the project's existing tickets. */
	importing: boolean;
	/** Reading its existing tickets stopped part way this week; approving it again restarts it. */
	importFailed: boolean;
}

/** The people assigned open tickets, each counted once, by what Curo made of them. Counts, never names. */
export interface JiraPeopleHealth {
	assignees: number;
	matched: number;
	invitedNotJoined: number;
	emailHidden: number;
	noSeat: number;
	app: number;
	checking: number;
}

export interface JiraWorkProgress {
	pendingReads: number;
	/** Ticket reads that failed in the last week. */
	failedReads: number;
	lastChangeAt: string | null;
	paused: boolean;
	/** Off, people are still matched but their work does not reach their plans. */
	claimsEnabled: boolean;
}

/** GET /api/org/integrations/jira/work-health (admins). */
export interface JiraWorkHealth {
	projects: JiraProjectHealth[];
	people: JiraPeopleHealth;
	work: JiraWorkProgress;
}

export interface OrgPlan {
	id: string;
	name: string;
	/** "USD" or "INR" — custom contracts use either. Never assume a symbol. */
	currency: string;
	billing_cycle: "monthly" | "quarterly" | "annual";
	pricing_model: "per_seat" | "flat_fee" | "graduated";
	/** null under flat_fee — that contract has no per-seat rate. */
	seat_price_cents: number | null;
	/** The whole charge per cycle under flat_fee; null under per_seat. */
	flat_amount_cents: number | null;
}

export interface OrgMe {
	org: OrgSummary;
	plan: OrgPlan | null;
	seats: SeatCounts;
	entitlements: Record<string, unknown>;
	you: { role: OrgRole; userId: string; name: string; email: string };
}

export interface OrgMember {
	id: string;
	org_id: string;
	user_id: string | null;
	email: string;
	user_name: string | null;
	role: OrgRole;
	seat_status: SeatStatus;
	/** The seat's team, at most one. null = unassigned. */
	team_id: string | null;
	invited_at: string;
	activated_at: string | null;
	deactivated_at: string | null;
	last_active_at: string | null;
	disclosure_ack_at: string | null;
}

/**
 * Bulk invite reports PER EMAIL rather than failing a whole batch on one bad
 * row — onboarding a company means pasting a list, and "something went wrong"
 * for fifty addresses is useless.
 */
export interface InviteResult {
	invited: OrgMember[];
	already_member: string[];
	invalid: string[];
	over_capacity: string[];
	seats: SeatCounts;
}

/**
 * Activity only. The Gateway never exposes content here — see orgAnalytics.ts.
 *
 * `tasks_engaged` is distinct tasks touched, NOT completions: planning never
 * writes a 'completed' status, so a completion metric would read 0 forever and
 * imply the team finishes nothing.
 */
export interface MemberActivity {
	user_id: string | null;
	email: string;
	name: string | null;
	seat_status: SeatStatus;
	role: OrgRole;
	joined_at: string | null;
	last_active_at: string | null;
	streak_count: number;
	active_days: number;
	minutes_spent: number;
	/** Tasks they opened or worked on. */
	tasks_engaged: number;
	/** Tasks the plan put in front of them, not counting ones it later replaced. */
	tasks_planned: number;
	/** Tasks they finished, counted on the day they finished them. */
	tasks_completed: number;
	boards_started: number;
	topics: string[];
}

export interface OrgUsageSummary {
	seats_active: number;
	learners_active_in_range: number;
	/** Invited but never claimed — a different problem from "claimed and idle". */
	never_activated: number;
	total_minutes: number;
	total_tasks_engaged: number;
	top_topics: { topic: string; learners: number }[];
	daily: { day: string; learners: number; minutes: number }[];
}

export interface OrgUsage {
	summary: OrgUsageSummary;
	members: MemberActivity[];
}

export interface AuditEntry {
	id: string;
	org_id: string;
	actor_user_id: string | null;
	actor_kind: "org_admin" | "system";
	action: string;
	target: string | null;
	detail: Record<string, unknown>;
	created_at: string;
}

export interface Disclosure {
	organization: string;
	acknowledged_at: string | null;
	shared: string[];
	not_shared: string[];
}

export interface DateRange {
	from?: string;
	to?: string;
}

/** Public, pre-auth. Presentation only — never seats, plan or members. */
export interface OrgBranding {
	name: string;
	slug: string;
	logo_url: string | null;
	accent: string | null;
}

/** A seat purchase, priced but not yet committed. */
export interface SeatTopUpQuote {
	currency: string;
	additional_seats: number;
	new_total_seats: number;
	/** Full-period rate for a seat at the NEW total. */
	unit_price_cents: number;
	previous_unit_price_cents: number;
	/** True when the purchase moves the org into a cheaper bracket. */
	crosses_bracket: boolean;
	days_remaining: number;
	period_days: number;
	/** Prorated per-seat price. unit x quantity is exactly total_cents. */
	prorated_unit_cents: number;
	total_cents: number;
	/** What these seats cost for a whole period at renewal. */
	full_period_cents: number;
}

export interface OrgInvoice {
	/** Our id, needed to ask the provider to re-check this invoice. */
	id: string;
	kind: "subscription" | "seat_topup" | "adjustment";
	pricing_model: "per_seat" | "flat_fee" | "graduated";
	/** null under flat_fee — that invoice has no seat quantity. */
	seat_quantity: number | null;
	unit_price_cents: number | null;
	total_cents: number;
	currency: string;
	status: "draft" | "issued" | "paid" | "expired" | "cancelled";
	short_url: string | null;
	period_start: string;
	period_end: string;
	issued_at: string | null;
	due_at: string | null;
	paid_at: string | null;
	/** How this invoice was meant to be collected, frozen at issue. */
	collection_mode?: CollectionMode;
	/** Why a mandate debit did not go through. Null when it did, or wasn't tried. */
	auto_charge_last_error?: string | null;
}

export type CollectionMode = "invoice" | "auto_charge";

/**
 * Whether this org's contract can carry a mandate at all.
 *
 * Answered from the contract, not from whether one exists — a Razorpay mandate
 * caps at ~₹99,999 per debit, which a quarterly B2B charge exceeds, so an org
 * has to be told BEFORE authorizing rather than when the first debit falls back.
 */
export interface AutoChargeAvailability {
	available: boolean;
	reason?: string;
	methods: string[];
	recommendedCapMinor?: number;
	/** What would fix it. Never just "no". */
	suggestion?: string;
	expected_debit_cents: number;
	currency: string;
	cycle: string;
}

export interface PaymentMethodState {
	collection_mode: CollectionMode;
	availability: AutoChargeAvailability;
	payment_method: OrgPaymentMethod | null;
	/** Razorpay refuses a recurring link without one, so the UI must ask first. */
	billing_phone_on_file: boolean;
}

export interface OrgPaymentMethod {
	id: string;
	status: "pending" | "active" | "paused" | "revoked" | "expired" | "failed";
	method: string;
	currency: string;
	/** The per-debit ceiling the org authorized. Cannot be raised. */
	max_amount_cents: number | null;
	bank_name: string | null;
	last4: string | null;
	expire_at: string | null;
	authorized_at: string | null;
	failure_reason: string | null;
	/** Only present while pending — the bank page to finish authorizing on. */
	registration_url: string | null;
}

export type ContentStatus =
	| "pending"
	| "extracting"
	| "embedding"
	| "ready"
	| "failed"
	| "removed";

export interface OrgContentSource {
	id: string;
	kind: "file" | "url" | "text";
	title: string;
	collection: string;
	source_url: string | null;
	file_name: string | null;
	byte_size: number | null;
	status: ContentStatus;
	error: string | null;
	/** Indexed sections. What the quota is measured in. */
	chunk_count: number;
	uploader_email?: string | null;
	uploader_name?: string | null;
	created_at: string;
	indexed_at: string | null;
}

export interface ScheduledSeats {
	/** Seats to apply at the next renewal. null = no change requested. */
	seats_scheduled: number | null;
}

export interface ContentUsage {
	used: number;
	quota: number;
}

/** Price preview for an org that has not started yet. */
export interface SetupQuote {
	seats: number;
	currency: string;
	cycle: string;
	unit_price_cents: number | null;
	subtotal_cents: number;
	discount_cents: number;
	discount_code: string | null;
	total_cents: number;
	/** Present when a code was supplied but cannot be used. */
	discount_error?: string;
}

// ─── Teams ───────────────────────────────────────────────────────────────────

/** How far out a stated intent looks. Coarse on purpose: a planning horizon,
 *  not a deadline, and a date would invite treating it as one. */
export type IntentHorizon = "month" | "quarter" | "half" | "year" | "ongoing";

/**
 * What an org or a team is trying to get better at.
 *
 * Every field is optional and `{}` is valid — "we have not said yet" is a real
 * state and the column's default. The Gateway validates and NORMALISES on the
 * way in (utils/orgIntent.ts): whitespace is collapsed, and domains come back
 * spelled the way the taxonomy spells them, so what is stored is not always
 * character-for-character what was typed. Render the response, not the draft.
 */
export interface OrgIntent {
	goal?: string;
	/** Domain LABELS ("Accounting & FP&A"), validated against the live taxonomy. */
	domains?: string[];
	/** Concepts inside those domains: "ASC 606", "month-end close". */
	focus?: string[];
	horizon?: IntentHorizon;
}

/** Limits the Gateway enforces. Mirrored here only so the UI can warn BEFORE a
 *  round trip; the server remains the authority and will still refuse. */
export const INTENT_LIMITS = {
	goalMax: 240,
	entryMax: 80,
	domainsMax: 6,
	focusMax: 12,
} as const;

export type TeamStatus = "active" | "archived";

export interface OrgTeam {
	id: string;
	org_id: string;
	/** Unique within the org, not globally. */
	slug: string;
	name: string;
	description: string | null;
	intent: OrgIntent;
	status: TeamStatus;
	created_at: string;
	updated_at: string;
	/** Present only on `?include=members`. */
	members?: OrgMember[];
}

/* ── Learning progress (activity radar) ─────────────────────────────────── */

/** A tier on the ladder: level 1, 2, 3 … with no top. */
export interface Tier {
	level: number;
	/** 0 ≤ progress < 1 through this tier. */
	progress: number;
	floor: number;
	next: number;
}

export interface RadarAxis {
	domain: string;
	key: string;
	/** "selected" = a domain the learner picked; "active" = where they actually spend time. */
	source: "selected" | "active";
	points: { now: number; before: number | null };
	tier: { now: Tier; before: Tier | null };
	/** Tiers from the centre of the chart. */
	position: { now: number; before: number | null };
	counts: {
		pieces: number;
		finished: number;
		highlighted: number;
		byFormat: Record<string, number>;
	};
}

export interface ActivityProjectionAxis {
	domain: string;
	key: string;
	source: "selected" | "active";
	points: { now: number; potential: number };
	tier: { now: Tier; potential: Tier };
	position: { now: number; potential: number };
}

export interface ActivityProjection {
	tasks: { total: number; completed: number; unfinished: number };
	totals: { credit: number; assigned: number; unassigned: number };
	additional: { credit: number; assigned: number; unassigned: number };
	axes: ActivityProjectionAxis[];
}

export interface LearnerRadar {
	userId: string;
	metric: "activity";
	version: string;
	asOf: string;
	compareTo: string | null;
	/** Rings on the chart: one beyond the highest tier reached. */
	rings: number;
	ladder: { base: number; growth: number };
	radarReady: boolean;
	axes: RadarAxis[];
	unassigned: {
		pieces: number;
		points: number;
		byFormat: Record<string, number>;
	};
	totals: { credit: number; assigned: number; unassigned: number };
	pickedDomains: string[];
	resolution: { events: number; bySource: Record<string, number> };
	projection: ActivityProjection | null;
}

/** The company's own radar: every member folded into one shape. */
export interface CompanyRadar {
	metric: "activity";
	members: number;
	activeMembers: number;
	rings: number;
	ladder: { base: number; growth: number };
	axes: {
		domain: string;
		key: string;
		/** Everything the org has earned here. */
		points: number;
		/** Members with any points here. */
		members: number;
		/** Points per active member — what the tier is read from. */
		average: number;
		tier: Tier;
		position: number;
		pieces: number;
	}[];
	totals: { credit: number; assigned: number; unassigned: number };
	projection: {
		tasks: { total: number; completed: number; unfinished: number };
		totals: { credit: number; assigned: number; unassigned: number };
		additional: { credit: number; assigned: number; unassigned: number };
		axes: Array<{
			domain: string;
			key: string;
			points: { now: number; potential: number };
			members: { now: number; potential: number };
			average: { now: number; potential: number };
			tier: { now: Tier; potential: Tier };
			position: { now: number; potential: number };
		}>;
	} | null;
}

/** One seat's row in the progress table. */
export interface MemberProgress {
	memberId: string;
	userId: string | null;
	email: string;
	name: string | null;
	role: OrgRole;
	seatStatus: SeatStatus;
	teamId: string | null;
	lastActiveAt: string | null;
	/** Null for a seat that was invited but never claimed: there is no learner yet. */
	radar: {
		rings: number;
		radarReady: boolean;
		totals: { credit: number; assigned: number; unassigned: number };
		pieces: number;
		axes: RadarAxis[];
	} | null;
}

export interface OrgProgress {
	company: CompanyRadar | null;
	members: MemberProgress[];
}

export interface MemberRadarResponse {
	member: MemberProgress;
	radar: LearnerRadar | null;
}

/** What is behind one axis. */
export interface DomainDetail {
	userId: string;
	key: string;
	domain: string;
	points: number;
	tier: Tier;
	concepts: { key: string; name: string; points: number; pieces: number }[];
	pieces: {
		key: string;
		title: string | null;
		format: string;
		/** How far they got, 0–1. */
		credit: number;
		points: number;
		concepts: number;
		lastAt: string;
	}[];
	/** Credit that reached this domain only because the piece had no concepts. */
	fromTags: number;
}
